/**
 * @render react
 * @name Breadcrumb
*/

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { Heading } from 'Components/molecules/Heading';
import { List } from 'Components/molecules/List';

import './_style.scss';

/**
 * Breadcrumb component
 * @param {Object|array} content - List of links
 * @param {Object} context - Extra information coming from Polaris provider.
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {number} headingSize - Size of the heading for the title
 * @param {bool}   isHeading - Whether to use Heading tags for the title
 * @param {Object} kicker - Text to display before the breadcrumb
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {string} title - Text to display before the kicker
 * @param {string} type - Type of the breadcrumb. Values can be sponsored or default for appropriate styling of the kicker.
 */

const Breadcrumb = ({
  content,
  context: {
    cssPrefix,
  },
  extraClassNames,
  headingSize,
  isHeading,
  kicker,
  loadStyles,
  title,
  type,
}) => (
  <div
    className={classNames({
      [`${cssPrefix}__breadcrumb`]: loadStyles,
      [`-${type}`]: true,
      ...extraClassNames,
    })}
  >
    {title && (
      isHeading ? (
        <Heading size={headingSize} extraClassNames={{ [`${cssPrefix}__breadcrumb--title`]: true }}>
          {title}
        </Heading>
      ) : (<p className={`${cssPrefix}__breadcrumb--title`}>{title}</p>)
    )}

    {kicker && (
      <span className={classNames({
        [`${cssPrefix}__breadcrumb--kicker`]: true,
      })}
      >
        {kicker}
      </span>
    )}

    {content.length > 0 && (
      <List content={content} />
    )}
  </div>
);

Breadcrumb.propTypes = {
  content: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    })),
    PropTypes.object,
  ]),
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  extraClassNames: PropTypes.objectOf(PropTypes.bool),
  headingSize: PropTypes.number,
  isHeading: PropTypes.bool,
  kicker: PropTypes.string,
  loadStyles: PropTypes.bool,
  title: PropTypes.string,
  type: PropTypes.oneOf(['default', 'sponsored']),
};

Breadcrumb.defaultProps = {
  content: [],
  context: {},
  extraClassNames: {},
  headingSize: 1,
  isHeading: true,
  kicker: '',
  loadStyles: true,
  title: '',
  type: 'default',
};

export default withPolaris(Breadcrumb);
